import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { getSocket } from "../../services/socket-io";
import useSound from "use-sound";
import { useWhiteLabel } from "../../context/WhiteLabel/WhiteLabelContext";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
}));

const getNotificationText = (message) => {
	const type = message.mediaType;
	const url = message.mediaUrl || message.body || "";
	if (type === "sticker") return "🎭 Sticker";
	if (type === "audio" || /\.(ogg|mp3|aac|m4a)(\?|$)/i.test(url)) return "🎵 Audio";
	if (type === "image" || /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url)) return "📷 Imagen";
	if (type === "video" || /\.(mp4|mov|avi|webm)(\?|$)/i.test(url)) return "🎥 Video";
	if (type === "document" || /\.pdf(\?|$)/i.test(url)) return "📄 Documento";
	return message.body || "";
};

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user, realUser } = useContext(AuthContext);
	const { whiteLabel } = useWhiteLabel();
	const appName = whiteLabel?.name || "WAIA";
	const isAdmin = user?.profile === "admin";
	const userQueueIds = (user?.queues || []).map(q => q.id);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [titleBlink, setTitleBlink] = useState(false);

	const [, setDesktopNotifications] = useState([]);

	// Admins see all notifications; regular users are scoped to their queues.
	const { tickets } = useTickets({
		withUnreadMessages: "true",
		...(isAdmin ? {} : { queueIds: JSON.stringify(userQueueIds) })
	});
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	// feat_316 — preferencias de notificación del usuario REAL. ⚠ En modo
	// "ver como usuario" (feat_299) AuthContext.user es el target; la preferencia
	// es del admin logueado. Se guarda en un ref porque el listener del socket
	// (efecto de abajo) no se re-suscribe cuando cambian las preferencias.
	const notificationPreferences = (realUser || user)?.notificationPreferences;
	const prefsRef = useRef(notificationPreferences);
	useEffect(() => {
		prefsRef.current = notificationPreferences;
	}, [notificationPreferences]);

	useEffect(() => {
		soundAlertRef.current = play;
		// feat_316: requestPermission() ya NO se pide en el mount (sin gesto de
		// usuario Chrome lo ignora, y con "denied" es un no-op silencioso). Ahora
		// se pide desde el click del toggle en NotificationSettingsButton.
	}, [play]);

	useEffect(() => {
		// Exclude the ticket currently being viewed — it's already read from
		// the user's perspective. Without this filter, a useTickets refetch
		// can re-add the open ticket and inflate the badge/title count.
		const filtered = tickets.filter(t => t.id !== ticketIdRef.current);
		setNotifications(prev => {
			// Skip update if the ticket IDs are identical — avoids a redundant
			// re-render (and potential flicker) when useTickets returns the same
			// list with only a new array reference.
			if (
				prev.length === filtered.length &&
				prev.every((t, i) => t.id === filtered[i]?.id)
			) return prev;
			return filtered;
		});
	}, [tickets]);

	// Blinking tab title when there are unread notifications
	useEffect(() => {
		const count = notifications.length;
		let blinkInterval = null;

		if (count > 0) {
			document.title = `(${count}) ${appName}`;
			blinkInterval = setInterval(() => {
				setTitleBlink(prev => {
					document.title = prev ? appName : `(${count}) ${appName}`;
					return !prev;
				});
			}, 1000);
		} else {
			document.title = appName;
		}

		return () => {
			if (blinkInterval) clearInterval(blinkInterval);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notifications.length, appName]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		// Superadmin has no companyId — skip socket connection entirely to
		// avoid joining a null/undefined notification room.
		if (user?.isSuperAdmin || !user?.companyId) return;

		const socket = getSocket();

		// Sin companyId: el backend lo toma del JWT (fix cross-tenant).
		const joinNotification = () => socket.emit("joinNotification");
		socket.on("connect", joinNotification);
		if (socket.connected) joinNotification();

		const handleTicket = data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		};

		const handleAppMessage = data => {
			// Filter by user's queues. Admins receive everything.
			// Non-admin: ticket must be assigned to me OR in one of my queues.
			// Null-queue unassigned tickets are admin-only (no !data.ticket.queueId bypass).
			const ticketIsForThisUser =
				isAdmin ||
				data.ticket.userId === user?.id ||
				(data.ticket.queueId && userQueueIds.includes(data.ticket.queueId));

			if (
				data.action === "create" &&
				!data.message.read &&
				ticketIsForThisUser
			) {
				// Don't add to notifications if user is actively viewing this ticket.
				// Without this guard, a message arriving on the open ticket inflates
				// the badge/title count even though the user is already reading it.
				const isActivelyViewed =
					data.ticket.id === ticketIdRef.current &&
					document.visibilityState === "visible";
				if (!isActivelyViewed) {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
						if (ticketIndex !== -1) {
							prevState[ticketIndex] = data.ticket;
							return [...prevState];
						}
						return [data.ticket, ...prevState];
					});
				}

				// Always notify via OS when tab is not visible
				if (document.visibilityState !== "visible" &&
					!data.ticket.isGroup &&
					(!data.ticket.userId || data.ticket.userId === user?.id)) {
					handleNotifications(data);
					return;
				}

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		};

		socket.on("ticket", handleTicket);
		socket.on("appMessage", handleAppMessage);

		return () => {
			// SINGLETON getSocket(): off() selectivo de los 3 listeners propios.
			// NUNCA socket.disconnect() — dejaría muda a toda la app.
			socket.off("connect", joinNotification);
			socket.off("ticket", handleTicket);
			socket.off("appMessage", handleAppMessage);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, user?.companyId, user?.isSuperAdmin, user?.profile, userQueueIds.join(",")]);

	// feat_316: el sonido y la notificación del navegador eran UNA sola función
	// (imposible tener uno sin el otro). Ahora son dos ramas independientes para
	// que los toggles del usuario sean realmente independientes.
	const playSoundIfEnabled = () => {
		if (soundAlertRef.current) soundAlertRef.current();
	};

	const showBrowserNotifIfEnabled = data => {
		const { message, contact, ticket } = data;
		if (!("Notification" in window)) return;
		// Sin permiso concedido, el constructor no muestra nada (y en algunos
		// navegadores lanza) — antes ese throw se tragaba también el sonido.
		if (Notification.permission !== "granted") return;

		const options = {
			body: `${getNotificationText(message)} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		let notification;
		try {
			notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contact.name}`,
				options
			);
		} catch (_) {
			return;
		}

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			historyRef.current.push(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});
	};

	// feat_316 — 3 niveles de decisión. ⚠ La LISTA y el BADGE nunca se silencian
	// (eso se resuelve arriba, en el handler del socket): silenciar es sobre el
	// sonido y el popup del SO. Si se silenciara el contador, un asesor perdería
	// trabajo pendiente sin enterarse.
	const handleNotifications = data => {
		const { contact } = data;
		if (!contact) return;

		const contactPriority = contact.notificationPriority || "normal";

		// Nivel 1 — el contacto manda: 'muted' nunca notifica.
		if (contactPriority === "muted") return;

		// Nivel 2 — 'priority' notifica SIEMPRE (ignora el silenciado global).
		if (contactPriority === "priority") {
			playSoundIfEnabled();
			showBrowserNotifIfEnabled(data);
			return;
		}

		// Nivel 3 — contacto normal: aplican las preferencias globales del usuario.
		// ⚠ Se leen de un ref, NO de la clausura: el listener del socket se
		// registra en un useEffect cuyas deps NO incluyen las preferencias → sin
		// el ref, el handler quedaría con el valor del render en que se suscribió.
		const prefs = prefsRef.current || {};

		if (prefs.mutedUntil === "permanent") return;
		if (prefs.mutedUntil) {
			const until = new Date(prefs.mutedUntil);
			if (!isNaN(until.getTime()) && until > new Date()) return;
		}

		// null/undefined = preferencia no configurada → activado (backward-compat:
		// los users existentes se comportan exactamente como antes).
		if (prefs.sound !== false) playSoundIfEnabled();
		if (prefs.browser !== false) showBrowserNotifIfEnabled(data);
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	// NOTE: NotificationTicket was previously defined here as a component
	// inside NotificationsPopOver. That caused every re-render (e.g. on new
	// socket message) to produce a NEW function reference → React treated it
	// as a different component type → unmounted+remounted TicketListItem on
	// every update → flicker + open modals/dialogs inside TicketListItem lost
	// their state. Replaced with an inline div handler to keep the element type
	// stable across renders.

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<div
								key={ticket.id}
								onClick={() => {
									handleClickAway();
									// Optimistically remove from notification list on click so the
									// badge count drops immediately without waiting for the socket event
									setNotifications(prev => prev.filter(t => t.id !== ticket.id));
								}}
							>
								<TicketListItem ticket={ticket} />
							</div>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
